/**
 * Opening gallery: the selected world's live preview fills the screen; branding upper left,
 * selection and "Enter this world" low on the left, the minimized player lower right. Search and
 * mood filters appear only when the catalog is large enough to need them.
 */
import { useMemo, useState } from 'react';
import type { WorldManifest } from '../framework/world/manifest';
import { assetUrl } from './assets';
import type { AppController, AppSnapshot } from './controller';
import { Button } from './ui/controls';
import { Icon } from './ui/icons';

const SEARCH_THRESHOLD = 6;

export function Gallery({ app, snap, entries }: { app: AppController; snap: AppSnapshot; entries: WorldManifest[] }) {
  const [query, setQuery] = useState('');
  const [mood, setMood] = useState<string | null>(null);
  const moods = useMemo(() => [...new Set(entries.flatMap((e) => e.moods))].slice(0, 8), [entries]);
  const showFilters = entries.length >= SEARCH_THRESHOLD;
  const filtered = entries.filter((e) => (!mood || e.moods.includes(mood)) && (!query || `${e.title} ${e.tagline} ${e.moods.join(' ')}`.toLowerCase().includes(query.toLowerCase())));
  const m = snap.manifest;
  const loading = snap.phase === 'loading' || snap.phase === 'booting';

  return (
    <>
      <header className="gbr-brand">
        <h1 className="gbr-brand-name">Glass Box Relaxation</h1>
        <span className="gbr-brand-sub">Living worlds to watch</span>
      </header>
      {m && (
        <section className="gbr-dock" aria-label="World selection">
          <div className="gbr-world-intro">
            <h2 className="gbr-world-title">{m.title}</h2>
            <p className="gbr-world-tagline">{m.tagline}</p>
            <div className="gbr-world-actions">
              {snap.phase === 'ready' && (
                <Button variant="primary" iconAfter="arrowRight" onClick={() => app.enterWorld()}>
                  Enter this world
                </Button>
              )}
              {loading && (
                <div className="gbr-progress" role="progressbar" aria-label="Preparing the world" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(snap.progress * 100)}>
                  <div className="gbr-progress-track">
                    <div className="gbr-progress-fill" style={{ width: `${Math.round(snap.progress * 100)}%` }} />
                  </div>
                  <span>{snap.progressLabel || 'Preparing'}…</span>
                </div>
              )}
              {snap.phase === 'error' && (
                <>
                  <span className="gbr-hint">This world could not start ({snap.error}).</span>
                  <Button variant="quiet" onClick={() => void app.loadWorld(snap.worldId)}>
                    Try again
                  </Button>
                </>
              )}
            </div>
            {showFilters && (
              <div className="gbr-row" style={{ justifyContent: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
                <label className="gbr-search">
                  <Icon name="search" size={16} />
                  <span className="gbr-visually-hidden">Search worlds</span>
                  <input value={query} placeholder="Search" onChange={(e) => setQuery(e.currentTarget.value)} />
                </label>
                <div className="gbr-chips" role="group" aria-label="Filter by mood">
                  {moods.map((md) => (
                    <button key={md} type="button" className="gbr-btn gbr-btn--chip" aria-pressed={mood === md} onClick={() => setMood(mood === md ? null : md)}>
                      {md}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {(entries.length > 1 || showFilters) && (
              <div className="gbr-catalog" role="list" aria-label="Worlds">
                {filtered.map((e) => (
                  <button
                    key={e.id}
                    role="listitem"
                    type="button"
                    className="gbr-card"
                    aria-current={e.id === snap.worldId}
                    onClick={() => e.id !== snap.worldId && void app.loadWorld(e.id)}
                  >
                    <img className="gbr-card-thumb" src={assetUrl(e.preview.poster)} alt="" loading="lazy" style={{ background: e.preview.palette[0] }} />
                    <span>
                      <span className="gbr-card-name">{e.title}</span>
                      <br />
                      <span className="gbr-card-moods">{e.devOnly ? 'development' : e.moods.slice(0, 2).join(' · ')}</span>
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </section>
      )}
    </>
  );
}
